import { startOperation } from '../telemetry/client';
import { useTranslation } from '../../i18n/react';
import { useEffect, useRef, useState } from 'react';
import { Icon } from '../../components/Icon';
import { detectImageFormat, imageFormats, IMAGE_ACCEPT, validateBatch, type ImageFormat, type OutputFormat } from './formats';
import type { ConversionProgress } from './media';
import './converter.css';

export function ImageConverterCard() {
  const tr = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const controller = useRef<AbortController | null>(null);
  const mounted = useRef(true);
  const [files, setFiles] = useState<File[]>([]);
  const [from, setFrom] = useState<ImageFormat | 'auto'>('auto');
  const [output, setOutput] = useState<OutputFormat>('webp');
  const [busy, setBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [progress, setProgress] = useState<ConversionProgress>({ done: 0, total: 0 });
  const [feedback, setFeedback] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; controller.current?.abort(); };
  }, []);

  function addFiles(incoming: File[]) {
    if (busy || !incoming.length) return;
    try {
      const next = [...files, ...incoming];
      validateBatch(next, from);
      setFiles(next);
      setFeedback(null);
    } catch (error) {
      setFeedback({ tone: 'error', message: error instanceof Error ? error.message : 'Không thể thêm ảnh.' });
    }
  }

  async function download() {
    if (controller.current) return;
    try { validateBatch(files, from); }
    catch (error) { setFeedback({ tone: 'error', message: (error as Error).message }); return; }
    const operation = startOperation('image-converter');
    const abort = new AbortController();
    controller.current = abort;
    setBusy(true);
    setFeedback(null);
    setProgress({ done: 0, total: files.length });
    try {
      const { convertImages, downloadImageResult } = await import('./media');
      const result = await convertImages(files, output, abort.signal, state => { if (mounted.current) setProgress(state); });
      if (!mounted.current || abort.signal.aborted) return;
      operation.complete({ exportBytes: result.blob.size });
      downloadImageResult(result);
      setFeedback({ tone: 'success', message: result.count > 1 ? 'Đã tạo ZIP chứa ' + result.count + ' ảnh.' : 'Đã tạo ' + result.name });
    } catch (error) {
      if (mounted.current) setFeedback({ tone: 'error', message: error instanceof Error ? error.message : 'Không thể chuyển ảnh.' });
    } finally {
      controller.current = null;
      if (mounted.current) setBusy(false);
    }
  }

  const note = output === 'svg' ? 'SVG giữ vector gốc; ảnh raster sẽ được nhúng.'
    : output === 'ico' ? 'ICO tối đa 256 px · nhiều ảnh tải trong ZIP.'
    : output === 'jpg' || output === 'bmp' ? 'Vùng trong suốt thành nền trắng · nhiều ảnh tải ZIP.'
    : 'Một ảnh tải trực tiếp · nhiều ảnh tải trong ZIP.';
  const status = busy ? progress.packing ? 'Đang nén ZIP…' : tr('Đang chuyển ' + progress.done + '/' + progress.total) + ' ảnh…' : feedback?.message || note;
  const sourceFormats = new Set(files.map(detectImageFormat));
  const animated = sourceFormats.has('gif') || sourceFormats.has('tiff') || sourceFormats.has('webp') || sourceFormats.has('avif') || sourceFormats.has('heic');
  return <article className="image-converter-card" aria-label="Convert image(s)" aria-busy={busy}>
    <h3>Convert image(s)</h3>
    <input ref={input} className="visually-hidden" type="file" multiple aria-label={tr("Chọn ảnh để chuyển đổi")} disabled={busy} accept={from === 'auto' ? IMAGE_ACCEPT : imageFormats.find(format => format.id === from)!.extensions.map(extension => '.' + extension).join(',')}
      onChange={event => { addFiles(Array.from(event.target.files || [])); event.target.value = ''; }} />
    <div className={'image-converter-dropzone' + (dragging ? ' is-dragging' : '')} onDragOver={event => { event.preventDefault(); if (!busy) setDragging(true); }} onDragLeave={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragging(false); }} onDrop={event => { event.preventDefault(); setDragging(false); addFiles(Array.from(event.dataTransfer.files)); }}>
      {files.length ? <>
        <ol className="image-converter-list" aria-label={tr("Ảnh đã chọn")}>{files.map((file, index) => <li key={index + '-' + file.name + '-' + file.lastModified}>
          <span className="image-converter-number">{index + 1}</span><Icon name="image" size={16}/>
          <span className="image-converter-file" title={file.name}>{file.name}<small>{tr(imageFormats.find(format => format.id === detectImageFormat(file))?.label)} · {(file.size / 1024).toFixed(0)} KB</small></span>
          <button type="button" className="image-converter-remove" disabled={busy} aria-label={'Xóa ảnh ' + (index + 1) + ': ' + file.name} onClick={() => { setFiles(current => current.filter((_, position) => position !== index)); setFeedback(null); }}><Icon name="close" size={14}/></button>
        </li>)}</ol>
        <div className="image-converter-list-footer"><button type="button" disabled={busy} onClick={() => input.current?.click()}>{tr("+ Thêm ảnh")}</button><span>{files.length}{tr("/50 ảnh")}</span></div>
        {animated && <p className="image-converter-frame-note">{tr("Đổi định dạng: lấy khung / trang đầu.")}</p>}
      </> : <button type="button" className="image-converter-placeholder" disabled={busy} onClick={() => input.current?.click()}>
        <span className="image-converter-plus" aria-hidden="true">+</span><span>Drag images to convert</span>
        <small>PNG, JPG, WebP, GIF, BMP, TIFF,<br/>ICO, SVG, AVIF, HEIC / HEIF</small>
      </button>}
    </div>
    <div className="image-converter-controls">
      <div className="image-converter-selects">
        <label className="visually-hidden" htmlFor="image-converter-from">{tr("Định dạng ảnh nguồn")}</label>
        <select id="image-converter-from" value={from} disabled={busy} onChange={event => { setFrom(event.target.value as ImageFormat | 'auto'); setFeedback(null); }}>
          <option value="auto">From: Auto</option>{imageFormats.map(format => <option key={format.id} value={format.id}>{'From: ' + format.label}</option>)}
        </select>
        <label className="visually-hidden" htmlFor="image-converter-to">{tr("Định dạng ảnh xuất")}</label>
        <select id="image-converter-to" value={output} disabled={busy} onChange={event => { setOutput(event.target.value as OutputFormat); setFeedback(null); }}>
          {imageFormats.filter(format => format.output).map(format => <option key={format.id} value={format.id}>{'To: ' + format.label}</option>)}
        </select>
      </div>
      <button className="image-converter-download" type="button" disabled={!files.length || busy} onClick={() => void download()}>{busy ? progress.packing ? tr('Nén ZIP…') : tr('Đang chuyển ' + progress.done + '/' + progress.total) : 'Download Image(s)'}</button>
      <p className={'image-converter-status' + (feedback ? ' is-' + feedback.tone : '')} role={feedback?.tone === 'error' ? 'alert' : 'status'} title={tr(status)}>{tr(status)}</p>
    </div>
  </article>;
}
